import { useState } from 'react'
import { Database, Server, BookOpen } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { StatCard } from '@/components/common/StatCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SourceHealthIndicator, GlossaryDeduplicationDialog } from '@/components/knowledge'
import { useKnowledgeSources, useKnowledgeStatus } from '@/api/knowledge'
import { usePermissions } from '@/hooks/usePermissions'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const sourceTypeBadgeVariant: Record<string, 'default' | 'secondary'> = {
  Confluence: 'default',
  AdoWiki: 'secondary',
}

export function KnowledgeSourcesPage() {
  const { data: sources, isLoading: sourcesLoading } = useKnowledgeSources()
  const { data: status, isLoading: statusLoading } = useKnowledgeStatus()
  const { role } = usePermissions()
  const [dedupOpen, setDedupOpen] = useState(false)

  const isLoading = sourcesLoading || statusLoading
  const isSuperAdmin = role === 'super_admin'

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        <PageHeader
          title="Knowledge Sources"
          description="Monitor connected knowledge source health and freshness"
          actions={
            isSuperAdmin ? (
              <Button onClick={() => setDedupOpen(true)}>Run Dedup</Button>
            ) : undefined
          }
        />
        <GlossaryDeduplicationDialog
          open={dedupOpen}
          onClose={() => setDedupOpen(false)}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Sources"
            value={sources?.length ?? 0}
            icon={Server}
            isLoading={sourcesLoading}
          />
          <StatCard
            label="Total Documents"
            value={sources?.reduce((sum, s) => sum + s.uniqueDocumentCount, 0) ?? 0}
            icon={Database}
            isLoading={sourcesLoading}
          />
          <StatCard
            label="Glossary Terms"
            value={status?.glossaryTermCount ?? 0}
            icon={BookOpen}
            isLoading={statusLoading}
          />
        </div>

        {isLoading && (
          <LoadingSpinner className="py-12" />
        )}

        {!isLoading && sources && sources.length > 0 && (
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Documents</TableHead>
                  <TableHead>Last Poll</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source.sourceInstanceId}>
                    <TableCell className="font-medium">{source.displayName}</TableCell>
                    <TableCell>
                      <Badge variant={sourceTypeBadgeVariant[source.sourceType] ?? 'outline'}>
                        {source.sourceType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{source.uniqueDocumentCount}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {source.lastPollTimestamp
                        ? formatDistanceToNow(new Date(source.lastPollTimestamp), { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <SourceHealthIndicator isHealthy={source.isHealthy} />
                    </TableCell>
                    <TableCell>
                      {source.lastError ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-sm text-red-600 dark:text-red-400 truncate max-w-[200px] block cursor-help">
                                {source.lastError}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-md">
                              <p className="text-xs">{source.lastError}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {!isLoading && sources && sources.length === 0 && (
          <EmptyState
            icon={Database}
            title="No sources configured"
            description="No knowledge sources have been registered yet."
          />
        )}
      </div>
    </PageShell>
  )
}
